import { apiClient } from './api-client'

export interface Permission {
  id: string
  resource: string
  action: string
  description?: string
  createdAt: string
  updatedAt: string
}

export interface Role {
  id: string
  name: string
  description?: string
  permissions: string[] // Array of permission IDs
  userCount: number
  isSystem: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateRoleData {
  name: string
  description: string
  permissionIds: string[]
}

export interface UpdateRolePermissionsData {
  permissionIds: string[]
}

class PermissionsService {
  private readonly baseUrl = '/api/auth'

  /**
   * Get all permissions
   */
  async getAllPermissions(): Promise<Permission[]> {
    const response = await apiClient.get(`${this.baseUrl}/permissions`)
    return response.data.data
  }

  /**
   * Get all roles with their permissions
   */
  async getAllRoles(): Promise<Role[]> {
    const response = await apiClient.get(`${this.baseUrl}/roles`)
    return response.data.data
  }

  /**
   * Get a specific role by ID
   */
  async getRoleById(roleId: string): Promise<Role> {
    const response = await apiClient.get(`${this.baseUrl}/roles/${roleId}`)
    return response.data.data
  }

  /**
   * Create a new custom role
   */
  async createRole(roleData: CreateRoleData): Promise<Role> {
    const response = await apiClient.post(`${this.baseUrl}/roles`, roleData)
    return response.data.data
  }

  /**
   * Update role permissions
   */
  async updateRolePermissions(
    roleId: string,
    data: UpdateRolePermissionsData
  ): Promise<Role> {
    const response = await apiClient.put(
      `${this.baseUrl}/roles/${roleId}/permissions`,
      data
    )
    return response.data.data
  }

  /**
   * Delete a custom role
   */
  async deleteRole(roleId: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/roles/${roleId}`)
  }
}

export const permissionsService = new PermissionsService()
